<template>
  <a-modal
    v-model:visible="visible"
    title="导出"
    :width="600"
    ok-text="导出"
    cancel-text="取消"
    :after-close="onClose"
    @cancel="() => (visible = false)"
    @ok="submit"
  >
    <a-form
      ref="form"
      :model="formState"
      :label-col="{ span: 4 }"
      :rules="formRules"
    >
      <a-form-item label="所属类型" name="cate">
        <a-select
          v-model:value="formState.cate"
          class=""
          :options="
            watchfaceWidgetCates.map((item) => ({ label: item, value: item }))
          "
          placeholder="请选择类型"
        />
      </a-form-item>
      <a-form-item label="名称" name="name">
        <a-input
          v-model:value="formState.name"
          class=""
          placeholder="请输入组名称"
        />
      </a-form-item>
      <a-form-item label="设备分类" name="deviceShapes">
        <a-select
          v-model:value="formState.deviceShapes"
          class="flex-1 mr-3"
          :options="watchfaceShapeOptions"
          :mode="'multiple'"
          placeholder="请选择支持的设备"
        />
      </a-form-item>
      <a-form-item label="预览图" name="preview">
        <div class="w-[350px] h-[200px] flex items-center justify-center">
          <ImagePicker
            :size="200"
            :icon-size="100"
            :border-color="'rgba(255, 255, 255, 0.2)'"
            :img="preview"
            :allow-preview="false"
            :deletable="false"
            @change="handleChange"
          />
        </div>
      </a-form-item>
    </a-form>
  </a-modal>
</template>

<script lang="ts" setup>
import { reactive, ref } from 'vue';
import { watchfaceShapeOptions } from '@/hooks';
import { FormExpose } from 'ant-design-vue/lib/form/Form';
import { Shape, ShapeType } from '@/services/shape';
import { useWatchfaceStore } from '@/store/use-watchface-store';
import { cloneDeep } from 'lodash-es';
import { useEditorStore } from '@/store/use-editor-store';
import { useImagesStore } from '@/store/use-images-store';
import {
  WatchfaceWidgetCate,
  WidgetService,
  watchfaceWidgetCates
} from '@/services/widget.service';
import { Modal, notification } from 'ant-design-vue';
import { ElectronApi } from '@/services/electron.api';
import { getImageBase64 } from '@/utils/fs/image.utils';

interface MyForm extends HTMLFormElement, FormExpose {}
const form = ref<MyForm | null>(null);
const watchfaceStroe = useWatchfaceStore();
const editorStore = useEditorStore();
const imageStore = useImagesStore();
watchfaceWidgetCates;
const formState: {
  cate: WatchfaceWidgetCate;
  name: string;
  deviceShapes: string[];
} = reactive({ cate: '背景', name: '', deviceShapes: [] });
const formRules = {
  name: [
    {
      required: true,
      message: '请输入内容'
    },
    {
      max: 16,
      message: '最多16个字符'
    }
  ],
  cate: [
    {
      required: true,
      message: '请选择类型'
    }
  ],
  deviceTypes: [
    {
      required: true,
      message: '请选择设备'
    }
  ]
};

const visible = ref(false);
const shapeId = ref('');
const preview = ref('');

const open = async (_shapeId: string) => {
  visible.value = true;
  shapeId.value = _shapeId;
  const shape = Shape.getShape(_shapeId);
  if (!shape) throw '图层不存在';
  formState.deviceShapes = [watchfaceStroe.watchfaceDevice?.shape ?? ''];
  formState.name = shape.attrs.name ?? '';
};
defineExpose({ open });

const handleChange = async (image: string) => {
  preview.value = (await getImageBase64(image)) ?? '';
};

const onClose = () => {
  form.value?.clearValidate();
  // form.value?.resetFields();
  formState.deviceShapes = [];
};

const submit = async () => {
  let valid = false;
  try {
    const names = Object.keys(formRules);
    await form.value?.validate(names);
    valid = true;
  } catch (error) {
    valid = false;
  }

  if (!valid) return;
  const colorid = editorStore.themeColorId;

  const shapeTypes: ShapeType[] = [];
  // 获取表达式layer
  const layer = Shape.getShape(shapeId.value)?.layer;
  if (!layer) return;
  const _layer = cloneDeep(layer);
  const stack = [_layer];
  while (stack.length) {
    const item = stack.shift();
    if (item?.children) {
      stack.push(...item.children);
    }
    if (item) shapeTypes.push(item.type as ShapeType);
    // 只保留第一个配色的资源
    if (item?.attrs.resources && item.attrs.resources) {
      const resourceNames = Object.keys(item.attrs.resources);
      resourceNames.forEach((resourceName) => {
        if (item.attrs.resources) {
          item.attrs.resources[resourceName] = {
            ['COLOR_ID']: item.attrs.resources[resourceName]?.[colorid]
          };
        }
      });
    }
  }

  // 获取使用资源
  const layerStr = JSON.stringify(_layer, undefined, 2);
  const images = imageStore.imgs.filter((item) =>
    layerStr.includes(item.basename)
  );

  try {
    const zipfile = await new WidgetService().export(
      layerStr,
      images.map((item) => {
        return {
          base64: item.value ?? '',
          basename: item.basename
        };
      }),
      { ...formState, preview: preview.value, shapeTypes }
    );
    if (!zipfile) throw '导出失败';
    visible.value = false;
    Modal.confirm({
      title: '表盘导出成功',
      content: '是否打开样例文件',
      okText: '打开',
      cancelText: '取消',
      onOk() {
        ElectronApi.showItemInFolder(zipfile);
      }
    });
  } catch (error) {
    console.log(error);

    notification.error({
      message: '表盘导出失败'
    });
  }
};
</script>

<style scoped></style>
